import BodyCopy from '@root/joinroot.com/src/components/body-copy';
import Colors from '@root/joinroot.com/src/utils/colors';
import Img from '@root/website-tooling/gatsby-image';
import LinkButton from '@root/joinroot.com/src/components/buttons/link-button';
import PropTypes from '@root/vendor/prop-types';
import useVisibilityAnalytics from '@root/core/src/hooks/use-visibility-analytics';
import { H3 } from '@root/joinroot.com/src/components/text';
import { StyleSheet } from '@root/core/src/utils/styles';

export default function ImageToutCard({
  body,
  button,
  image,
  title,
  id,
  buttonComponent = LinkButton,
}) {
  const { nodeRef, trackClick } = useVisibilityAnalytics(`IMAGE_TOUT_${id}`);
  const ButtonComponent = buttonComponent;

  return (
    <div
      css={[baseStyles.imageTout, cardStyles.imageTout]}
      ref={nodeRef}
    >
      <div css={[baseStyles.imageContainer, cardStyles.imageContainer]}>
        <Img
          alt={image.imageAltText ?? ''}
          css={[baseStyles.image, cardStyles.image]}
          fluid={image.fluid}
        />
      </div>
      <div css={[baseStyles.textContainer, cardStyles.textContainer]}>
        {title && <H3 css={[baseStyles.title, cardStyles.title]}>{title}</H3>}
        <BodyCopy
          content={body}
          css={[baseStyles.body, cardStyles.body]}
        />
        {button &&
          <ButtonComponent
            onClick={() => trackClick(`CTA_${button.id}`)}
            to={button.link}
            type={button.type}
          >
            {button.text}
          </ButtonComponent>
        }
      </div>
    </div>
  );
}

const baseStyles = StyleSheet.create({
  imageTout: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    paddingBottom: 35,
    textAlign: 'center',
    '&:last-of-type': {
      paddingBottom: 0,
    },
  },
  imageContainer: {
    width: '100%',
    marginBottom: 35,
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  title: {
    marginTop: 0,
    marginBottom: 15,
  },
  body: {
    marginBottom: 35,
    '&:last-child': {
      marginBottom: 0,
    },
  },
});

const cardStyles = StyleSheet.create({
  imageTout: {
    width: 'auto',
    minWidth: 255,
    maxWidth: 335,
    marginBottom: 35,
    paddingBottom: 35,
    borderRadius: 6,
    boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.25)',
    backgroundColor: Colors.white(),
    '&:last-of-type': {
      marginBottom: 0,
      paddingBottom: 35,
    },
    '.sliding &': {
      marginRight: 20,
    },
    '.sliding.one-tout &': {
      marginRight: 0,
    },
    '@media screen and (min-width: 375px)': {
      minWidth: 310,
    },
    '@media screen and (min-width: 768px)': {
      '.four-touts &, .four-plus-touts &': {
        marginRight: 20,
      },
      '.four-touts &:nth-of-type(2n), .four-plus-touts &:nth-of-type(2n)': {
        marginRight: 0,
      },
      '.sliding.four-touts &, .sliding.four-plus-touts &': {
        marginRight: 20,
      },
    },
    '@media screen and (min-width: 1280px)': {
      minWidth: 335,
      '.four-plus-touts &:nth-of-type(2n)': {
        marginRight: 20,
      },
      '.four-plus-touts &:nth-of-type(3n)': {
        marginRight: 0,
      },
      '.sliding.four-plus-touts &:nth-of-type(3n)': {
        marginRight: 20,
      },
    },
    '@media screen and (min-width: 1440px)': {
      '.four-touts &:nth-of-type(2n), .four-plus-touts &:nth-of-type(3n)': {
        marginRight: 20,
      },
      '.four-touts &:nth-of-type(4n), .four-plus-touts &:nth-of-type(4n)': {
        marginRight: 0,
      },
      '.sliding.four-touts &:nth-of-type(4n), .sliding.four-plus-touts &:nth-of-type(4n)': {
        marginRight: 20,
      },
    },
  },
  image: {
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6,
  },
  textContainer: {
    paddingLeft: 35,
    paddingRight: 35,
  },
});

ImageToutCard.propTypes = {
  body: PropTypes.node,
  button: PropTypes.shape({
    id: PropTypes.string.isRequired,
    link: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  }),
  buttonComponent: PropTypes.func,
  id: PropTypes.string.isRequired,
  image: PropTypes.shape({
    imageAltText: PropTypes.string,
    fluid: PropTypes.object.isRequired,
  }).isRequired,
  title: PropTypes.node,
};
